import { Routes, Route, Navigate } from "react-router-dom";
import Shell from "@/layout/Shell";
import KpiGrid from "@/components/KpiGrid";
import Queue from "@/components/Queue";
import NewTicketDialog from "@/components/NewTicketDialog";
import KnownIssues from "@/components/KnownIssues";
import { useAuth } from "@/lib/auth";
import { useFetch } from "@/lib/useFetch";
import api from "@/lib/api";
import { hoursAgo } from "@/lib/mock";
import { Sparkles, Ticket, CheckCircle2, Clock } from "lucide-react";
import Help from "@/pages/Help";
import MyAssets from "@/pages/cam/MyAssets";

function usePortalData() {
  const tickets = useFetch(() => api.tickets({ limit: 100, scope: "mine" }).then((r) => r?.tickets || []), []);
  const cats = useFetch(() => api.categories().then((r) => r?.categories || []), []);
  return { tickets, cats };
}

function Overview() {
  const { user } = useAuth();
  const { tickets, cats } = usePortalData();

  const list = tickets.data || [];
  const open = list.filter((t) => !["closed"].includes(t.status)).length;
  const resolved = list.filter((t) => ["resolved", "closed"].includes(t.status)).length;
  const inProgress = list.filter((t) => t.status === "in_progress").length;

  return (
    <Shell role="portal" title={`Welcome back${user?.name ? `, ${user.name.split(" ")[0]}` : ""}`} subtitle="Here's what's happening with your requests." actions={<NewTicketDialog categories={cats.data || []} onCreated={tickets.reload} />}>
      <KpiGrid
        items={[
          { label: "My open",     value: open,       accent: "#0047FF", note: "in progress + queued" },
          { label: "In progress", value: inProgress, accent: "#F59E0B", note: "being worked on" },
          { label: "Resolved",    value: resolved,   accent: "#10B981", note: "all-time" },
          { label: "Total",       value: list.length, accent: "#22D3EE", note: "you've raised" },
        ]}
      />

      {/* v8.3 — renders nothing if there are no active known issues; see KnownIssues.jsx */}
      <KnownIssues />

      <div className="grid lg:grid-cols-[1.4fr_0.9fr] gap-6">
        <div className="glass rounded-2xl p-5" data-testid="portal-quick">
          <div className="flex items-start justify-between">
            <div>
              <div className="overline">Need help?</div>
              <h3 className="font-display text-3xl font-extrabold tracking-tighter mt-1">Raise a ticket in 20 seconds.</h3>
              <p className="text-sm text-muted-foreground mt-2 max-w-md">
                Pick a service. Describe the issue. We auto-route it to the right technician in under a minute.
              </p>
            </div>
            <Sparkles className="h-8 w-8 text-cobalt-500 shrink-0" />
          </div>
          <div className="mt-5 flex flex-wrap gap-3 items-start">
            <NewTicketDialog categories={cats.data || []} onCreated={tickets.reload} />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 flex-1 min-w-[260px]">
              {(cats.data || []).slice(0, 4).map((c) => (
                <div key={c.id} data-testid={`quick-cat-${c.id}`} className="text-sm rounded-xl bg-white/70 dark:bg-white/5 border border-white/60 dark:border-white/10 py-2 px-3 flex items-center gap-2">
                  <span className="h-6 w-6 rounded-md grid place-items-center bg-cobalt-500/10 text-xs">{c.name?.[0] || "•"}</span>
                  <span className="font-medium truncate">{c.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="glass rounded-2xl p-5" data-testid="portal-latest">
          <div className="overline">Latest</div>
          <h3 className="font-display text-2xl font-extrabold tracking-tighter mt-1">Your recent tickets</h3>
          <ul className="mt-4 space-y-3">
            {list.length === 0 && <li className="text-sm text-muted-foreground">You haven&apos;t raised any tickets yet.</li>}
            {list.slice(0, 5).map((t) => (
              <li key={t.id} className="flex gap-3 items-start">
                <span className="mt-1 h-2 w-2 rounded-full bg-cobalt-500" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate"><span className="text-mono text-xs">{t.ticket_no}</span> · {t.title}</div>
                  <div className="text-xs text-muted-foreground capitalize">{t.status?.replace("_"," ")} · {hoursAgo(t.created_at)} ago</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">Your tickets</div>
            <h3 className="font-display text-2xl font-extrabold tracking-tighter">All submissions</h3>
          </div>
        </div>
        <Queue tickets={list} categories={cats.data || []} onChanged={tickets.reload} requesterOnly />
      </div>
    </Shell>
  );
}

function MyTickets() {
  const { tickets, cats } = usePortalData();
  return (
    <Shell role="portal" title="My Tickets" subtitle="Every request, sorted." actions={<NewTicketDialog categories={cats.data || []} onCreated={tickets.reload} />}>
      <Queue tickets={tickets.data || []} categories={cats.data || []} onChanged={tickets.reload} requesterOnly />
    </Shell>
  );
}

function ActivityPage() {
  const tickets = useFetch(() => api.tickets({ limit: 200, scope: "mine" }).then((r) => r?.tickets || []), []);
  const list = tickets.data || [];
  return (
    <Shell role="portal" title="Activity" subtitle="Everything that happened.">
      <div className="glass rounded-2xl p-5" data-testid="portal-activity-page">
        <div className="overline">Timeline</div>
        <h3 className="font-display text-3xl font-extrabold tracking-tighter mt-1">Recent events</h3>
        <ol className="relative border-l border-zinc-200 dark:border-zinc-800 pl-6 mt-6 space-y-6">
          {list.length === 0 && <li className="text-sm text-muted-foreground">No activity yet.</li>}
          {list.map((t) => (
            <li key={t.id}>
              <span className="absolute -left-[7px] mt-1 h-3 w-3 rounded-full ring-4 ring-white dark:ring-zinc-950 bg-cobalt-500" />
              <div className="flex items-center gap-2">
                <div className="text-sm"><span className="text-mono text-xs">{t.ticket_no}</span> · <b>{t.title}</b></div>
                <span className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground ml-auto">{hoursAgo(t.updated_at || t.created_at)} ago</span>
              </div>
              <div className="text-sm text-muted-foreground capitalize">{t.status?.replace("_"," ")}</div>
            </li>
          ))}
        </ol>
      </div>
    </Shell>
  );
}

export default function Portal() {
  return (
    <Routes>
      <Route index element={<Overview />} />
      <Route path="tickets" element={<MyTickets />} />
      <Route path="my-assets" element={<MyAssets />} />
      <Route path="help" element={<Help />} />
      <Route path="activity" element={<ActivityPage />} />
      {/* unknown sub-path -> go home instead of rendering nothing */}
      <Route path="*" element={<Navigate to="/portal" replace />} />
    </Routes>
  );
}
